import type { FC } from "react";
import { Controller } from "react-hook-form";

import type { AddressFormProviderProps } from "@ui/components/AddressFormProvider";
import { useFormContext } from "@ui/components/Form";
import { TextInput } from "@ui/components/Inputs";
import { useTranslations } from "@ui/providers/useTranslations";

import styles from "./UnitedArabEmiratesForm.module.scss";

type UnitedArabEmiratesFormProps = Pick<AddressFormProviderProps, "fieldName">;

const fieldNameDefault = (name: string) => name;

export const UnitedArabEmiratesForm: FC<UnitedArabEmiratesFormProps> = ({ fieldName = fieldNameDefault }) => {
  const { __ } = useTranslations();

  const { control } = useFormContext();

  return (
    <div className={styles.wrapper}>
      <Controller
        control={control}
        name={fieldName("neighborhood")}
        rules={{
          required: __("form_validation.field_required"),
        }}
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            data-testid="neighborhoodUAE"
            required
            containerClassName={styles.neighborhood}
            label={__("address_fields.neighborhood")}
            placeholder={__("address_fields.neighborhood_placeholder")}
            isError={!!fieldState?.error}
            errorText={fieldState?.error?.message}
            autoComplete="address-level3"
          />
        )}
      />
      <Controller
        control={control}
        name={fieldName("streetName")}
        rules={{
          required: __("form_validation.field_required"),
        }}
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            data-testid="streetNameUAE"
            containerClassName={styles.streetName}
            required
            label={__("address_fields.street_name")}
            placeholder={__("address_fields.street_name_placeholder")}
            isError={!!fieldState?.error}
            errorText={fieldState?.error?.message}
            autoComplete="address-line1"
          />
        )}
      />
      <Controller
        control={control}
        name={fieldName("premise")}
        render={({ field }) => (
          <TextInput
            {...field}
            containerClassName={styles.premise}
            data-testid="premiseUAE"
            label={__("address_fields.premise")}
            placeholder={__("address_fields.premise_placeholder")}
            autoComplete="address-line2"
          />
        )}
      />
      <Controller
        control={control}
        name={fieldName("streetAdditional")}
        render={({ field }) => (
          <TextInput
            {...field}
            containerClassName={styles.streetNumber}
            data-testid="streetAdditionalUAE"
            label={__("address_fields.street_additional")}
            placeholder={__("address_fields.street_additional_placeholder")}
            autoComplete="address-line3"
          />
        )}
      />
      <Controller
        control={control}
        name={fieldName("city")}
        rules={{
          required: __("form_validation.field_required"),
        }}
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            data-testid="cityUAE"
            required
            containerClassName={styles.city}
            label={__("address_fields.city")}
            placeholder={__("address_fields.city_placeholder")}
            isError={!!fieldState?.error}
            errorText={fieldState?.error?.message}
            autoComplete="address-level2"
          />
        )}
      />
      <Controller
        control={control}
        name={fieldName("landmark")}
        render={({ field }) => (
          <TextInput
            {...field}
            data-testid="landmarkUAE"
            containerClassName={styles.landmark}
            label={__("address_fields.landmark")}
            placeholder={__("address_fields.landmark_placeholder")}
            autoComplete="off"
          />
        )}
      />
    </div>
  );
};
